"use client";

import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SimpleLoader } from "@/components/ui/simple-loader";
import { Search } from "lucide-react";

interface PropertySelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (propertyIds: string[]) => void;
  selectedPropertyIds?: string[];
}

export function PropertySelectorDialog({
  open,
  onOpenChange,
  selectedPropertyIds = [],
  onSelect,
}: PropertySelectorDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(
    new Set()
  );

  const properties = useQuery(
    api.properties.getAllBaseProperties,
    open
      ? {
          filters: {}, // show all properties including hidden and drafts
        }
      : "skip"
  );

  useEffect(() => {
    if (open) {
      setLocalSelectedIds(new Set(selectedPropertyIds));
      setSearchTerm("");
    }
  }, [open]);

  const filteredProperties =
    properties && searchTerm
      ? properties.filter(
          (p) =>
            p.property_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.reff_id.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : properties || [];

  const handleToggleProperty = (propertyId: string) => {
    const newSelected = new Set(localSelectedIds);
    if (newSelected.has(propertyId)) {
      newSelected.delete(propertyId);
    } else {
      newSelected.add(propertyId);
    }
    setLocalSelectedIds(newSelected);
  };

  const handleConfirm = () => {
    onSelect(Array.from(localSelectedIds));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Select Properties</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="space-y-2 flex-shrink-0">
            <Label htmlFor="property-search">Search Properties</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="property-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or reference ID..."
                className="pl-9"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 border rounded-md overflow-y-auto">
            {properties === undefined ? (
              <div className="flex items-center justify-center p-8">
                <SimpleLoader message="Loading properties..." />
              </div>
            ) : filteredProperties.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {searchTerm
                  ? "No properties found matching your search"
                  : "No properties available"}
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {filteredProperties.map((property) => {
                  const isSelected = localSelectedIds.has(property._id);
                  return (
                    <div
                      key={property._id}
                      className="flex items-center space-x-3 p-3 rounded-md border hover:bg-accent cursor-pointer"
                      onClick={() => handleToggleProperty(property._id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() =>
                          handleToggleProperty(property._id)
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {property.property_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {property.reff_id}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="text-sm text-muted-foreground flex-shrink-0">
            {localSelectedIds.size} property
            {localSelectedIds.size !== 1 ? "ies" : ""} selected
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm}>
            Insert Selected ({localSelectedIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
